<template>
  <div class="visibility-scheduler border-2 border-slate-200 rounded-2xl p-6 bg-white shadow-sm">
    <!-- En-tête -->
    <div class="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">

      <div>
        <h3 class="font-bold text-slate-900 text-sm">Planification de Visibilité</h3>
        <p class="text-xs text-slate-500">Configurez les jours d'affichage de cette file</p>
      </div>
    </div>

    <!-- Toggle principal -->
    <label class="flex items-start gap-3 mb-6 cursor-pointer group p-4 rounded-xl bg-slate-50 hover:bg-blue-50 transition-all border border-slate-200 hover:border-blue-300">
      <input
        type="checkbox"
        v-model="localSchedule.enabled"
        class="w-5 h-5 mt-0.5 text-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer transition-all"
      />
      <div class="flex-1">
        <span class="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors block mb-1">
          Limiter la visibilité à certains jours
        </span>
        <p class="text-xs text-slate-600 leading-relaxed">
          {{ localSchedule.enabled
            ? 'La file sera visible uniquement les jours sélectionnés ci-dessous'
            : 'Désactivé : la file sera visible tous les jours de la semaine' }}
        </p>
      </div>
    </label>

    <!-- Grille des jours -->
    <div v-if="localSchedule.enabled" class="space-y-3">
      <p class="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wider flex items-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Sélectionnez les jours de visibilité
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label
          v-for="day in days"
          :key="day.key"
          class="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all border-2"
          :class="{
            'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 shadow-sm': localSchedule.days[day.key],
            'bg-slate-50 border-slate-200 hover:border-slate-300 hover:bg-slate-100': !localSchedule.days[day.key]
          }"
        >
          <input
            type="checkbox"
            v-model="localSchedule.days[day.key]"
            class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
          />


          <!-- Label du jour -->
          <div class="flex-1">
            <span
              class="text-sm font-bold block transition-colors"
              :class="{
                'text-blue-700': localSchedule.days[day.key],
                'text-slate-700': !localSchedule.days[day.key]
              }"
            >
              {{ day.label }}
            </span>
            <span class="text-xs text-slate-500">{{ day.sublabel }}</span>
          </div>

          <!-- Badge Aujourd'hui -->
          <span
            v-if="isToday(day.key)"
            class="text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wide"
            :class="{
              'bg-blue-600 text-white': localSchedule.days[day.key],
              'bg-slate-300 text-slate-700': !localSchedule.days[day.key]
            }"
          >
            Aujourd'hui
          </span>
        </label>
      </div>
    </div>

    <!-- Message d'avertissement : Aucun jour sélectionné -->
    <div
      v-if="localSchedule.enabled && !hasAnyDayEnabled"
      class="mt-5 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl flex items-start gap-3 shadow-sm"
    >
      <svg class="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div class="text-sm">
        <p class="font-bold text-amber-800 mb-1">⚠️ Aucun jour sélectionné</p>
        <p class="text-xs text-amber-700 leading-relaxed">
          Cette file ne sera jamais visible. Veuillez sélectionner au moins un jour de visibilité.
        </p>
      </div>
    </div>

    <!-- Message informatif : Option désactivée -->
    <div
      v-if="!localSchedule.enabled"
      class="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl flex items-center gap-3"
    >
      <svg class="w-5 h-5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-blue-800">
        Cette file d'attente sera visible <span class="font-bold">tous les jours</span> de la semaine
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QueueVisibilityScheduler',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        enabled: false,
        days: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }
      })
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      days: [
        { key: 'monday', label: 'Lundi', sublabel: 'Semaine' },
        { key: 'tuesday', label: 'Mardi', sublabel: 'Semaine' },
        { key: 'wednesday', label: 'Mercredi', sublabel: 'Semaine' },
        { key: 'thursday', label: 'Jeudi', sublabel: 'Semaine' },
        { key: 'friday', label: 'Vendredi', sublabel: 'Semaine' },
        { key: 'saturday', label: 'Samedi', sublabel: 'Week-end' },
        { key: 'sunday', label: 'Dimanche', sublabel: 'Week-end' }
      ],
      localSchedule: this.getInitialSchedule(),
      isUpdating: false
    };
  },
  computed: {
    hasAnyDayEnabled() {
      return Object.values(this.localSchedule.days).some(day => day === true);
    }
  },
  watch: {
    localSchedule: {
      deep: true,
      handler(newVal) {
        if (!this.isUpdating) {
          this.$emit('update:modelValue', newVal);
        }
      }
    },
    modelValue: {
      deep: true,
      handler(newVal) {
        // Éviter la boucle infinie lors de la mise à jour depuis le parent
        if (!this.isUpdating && newVal) {
          this.isUpdating = true;
          this.localSchedule = this.getInitialSchedule();
          this.$nextTick(() => {
            this.isUpdating = false;
          });
        }
      }
    }
  },
  methods: {
    getInitialSchedule() {
      if (this.modelValue && typeof this.modelValue === 'object') {
        return {
          enabled: this.modelValue.enabled || false,
          days: {
            monday: this.modelValue.days?.monday !== false,
            tuesday: this.modelValue.days?.tuesday !== false,
            wednesday: this.modelValue.days?.wednesday !== false,
            thursday: this.modelValue.days?.thursday !== false,
            friday: this.modelValue.days?.friday !== false,
            saturday: this.modelValue.days?.saturday !== false,
            sunday: this.modelValue.days?.sunday !== false
          }
        };
      }
      return {
        enabled: false,
        days: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }
      };
    },
    isToday(dayKey) {
      try {
        const today = new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          timeZone: 'Europe/Paris'
        }).format(new Date()).toLowerCase();
        return dayKey === today;
      } catch (error) {
        const today = new Date().getDay(); // 0=dimanche, 1=lundi, ..., 6=samedi
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6
        };
        return dayMap[dayKey] === today;
      }
    },
    getDayIcon(dayKey) {
      const icons = {
        monday: 'IconMonday',
        tuesday: 'IconTuesday',
        wednesday: 'IconWednesday',
        thursday: 'IconThursday',
        friday: 'IconFriday',
        saturday: 'IconSaturday',
        sunday: 'IconSunday'
      };
      return icons[dayKey];
    }
  },
  components: {

  }
};
</script>

<style scoped>
.visibility-scheduler {
  transition: all 0.2s ease;
}

@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }
}
</style>
